/* eslint-disable jsx-a11y/no-autofocus */
import React, { FC, useState } from "react";
import { Box, Checkbox, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Badge from "../../../../assets/hamaspik/Badge";
import { PifApprovalRoleMeta } from "../types";
import { PifSignSummary } from "./approvalHelpers";
import "./PifApprovalReview.scss";

interface PifApprovalDecisionPanelProps {
  roleMeta: PifApprovalRoleMeta;
  /** The hire at a glance, shown in the Preview & Sign step before the signature is recorded. */
  summary: PifSignSummary;
  /** Resolves true once the signature is recorded; false keeps the step open for a retry. */
  onSign: () => Promise<boolean>;
  onSendBack: (note: string) => void;
  onDecline: (note: string) => void;
}

type PanelMode = "idle" | "sign" | "sendback" | "decline";

// Portal_PostIFSignatures.Reason is varchar(500) and the API binds @Reason at 500, so a longer note
// lost its ending on save (Bug 84563). The box stops at the same length.
const NOTE_MAX_LENGTH = 500;

const PifApprovalDecisionPanel: FC<PifApprovalDecisionPanelProps> = ({ roleMeta, summary, onSign, onSendBack, onDecline }) => {
  const [mode, setMode] = useState<PanelMode>("idle");
  const [note, setNote] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [signFailed, setSignFailed] = useState(false);
  const noteValid = note.trim().length > 0;

  const handleCancel = () => {
    setMode("idle");
    setNote("");
    setReviewed(false);
    setSignFailed(false);
  };

  const notePlaceholder = mode === "decline" ? "Explain why you're declining this hire" : "What needs to change";

  const handleConfirm = () => {
    if (mode === "sendback") onSendBack(note);
    if (mode === "decline") onDecline(note);
    setMode("idle");
    setNote("");
  };

  // Epic 82837: no one-click sign. Sign only records once the approver has ticked that they
  // reviewed the summary, and a failed call leaves the step open (and the timeline unmoved) so
  // they can simply try again.
  const handleSign = async () => {
    setSignFailed(false);
    const recorded = await onSign();
    if (!recorded) setSignFailed(true);
  };

  const { band } = summary;

  return (
    <Box className="pif-approval-decision">
      {mode === "sign" && (
        <Box className="pif-approval-decision__preview" data-testid="postif-approval-preview">
          <Typography className="pif-approval-decision__preview-title">Preview &amp; Sign</Typography>
          <Box className="pif-approval-decision__preview-grid">
            {summary.rows.map((r) => (
              <Box key={r.k} className="pif-approval-decision__preview-cell">
                <Typography className="pif-approval-decision__preview-k">{r.k}</Typography>
                <Box className="pif-approval-decision__preview-vrow">
                  <Typography className="pif-approval-decision__preview-v">{r.v}</Typography>
                  {r.pill && (
                    <Badge variant={r.pillTone === "warn" ? "warning" : "success"} size="sm">
                      {r.pill}
                    </Badge>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          {band && (
            // Display only: the supervisor already attested an out-of-band rate, so the approver
            // is shown it plainly but is not asked to confirm it a second time.
            <Typography
              className={`pif-approval-decision__preview-band${band.outOfBand ? " pif-approval-decision__preview-band--warn" : ""}`}
              data-testid="postif-approval-preview-band"
            >
              Hiring band ${band.hiringMin}–${band.hiringMax} · position range ${band.positionMin}–${band.positionMax}
              {band.outOfBand ? " · outside band" : ""}
            </Typography>
          )}
          <Box component="label" className="pif-approval-decision__confirm">
            <Checkbox
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
              inputProps={{ "data-testid": "postif-approval-reviewed" } as React.InputHTMLAttributes<HTMLInputElement>}
            />
            <Typography className="pif-approval-decision__confirm-text">
              Confirm you&apos;ve reviewed this hire before signing.
            </Typography>
          </Box>
          {signFailed && (
            <Typography role="alert" className="pif-approval-decision__error" data-testid="postif-approval-sign-error">
              Couldn&apos;t record your signature, try again.
            </Typography>
          )}
        </Box>
      )}

      {(mode === "sendback" || mode === "decline") && (
        <Box className="pif-approval-decision__note">
          <TextField
            // The id is what makes MUI link helperText (the "n / 500" counter) via aria-describedby.
            id="postif-approval-note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
            placeholder={notePlaceholder}
            helperText={`${note.length} / ${NOTE_MAX_LENGTH}`}
            inputProps={{ maxLength: NOTE_MAX_LENGTH, "aria-label": notePlaceholder, "data-testid": "postif-approval-note" }}
          />
          <Typography className="pif-approval-decision__note-hint">Stored on the audit trail and visible to everyone in the chain.</Typography>
        </Box>
      )}

      <Box className="pif-approval-decision__actions">
        {mode === "idle" && (
          <>
            <Button variant="text" color="secondary" onClick={() => setMode("decline")} data-testid="postif-approval-decline">
              Decline this hire
            </Button>
            <Button variant="outlined" onClick={() => setMode("sendback")} data-testid="postif-approval-sendback">
              Send back for changes
            </Button>
            <Button variant="contained" onClick={() => setMode("sign")} data-testid="postif-approval-sign">
              Preview &amp; Sign
            </Button>
          </>
        )}
        {mode === "sign" && (
          // Epic 82837: the Preview & Sign panel carries all three decisions — Sign / Send back /
          // Decline — so the approver can act on what the summary shows without backing out first.
          <>
            <Button variant="text" onClick={handleCancel} data-testid="postif-approval-cancel">
              Cancel
            </Button>
            <Button variant="text" color="secondary" onClick={() => setMode("decline")} data-testid="postif-approval-preview-decline">
              Decline this hire
            </Button>
            <Button variant="outlined" onClick={() => setMode("sendback")} data-testid="postif-approval-preview-sendback">
              Send back for changes
            </Button>
            <Button variant="contained" disabled={!reviewed} onClick={handleSign} data-testid="postif-approval-sign-confirm">
              {signFailed ? "Try again" : roleMeta.signCta}
            </Button>
          </>
        )}
        {(mode === "sendback" || mode === "decline") && (
          <>
            <Button variant="text" onClick={handleCancel} data-testid="postif-approval-cancel">
              Cancel
            </Button>
            <Button
              variant="contained"
              color={mode === "decline" ? "secondary" : "primary"}
              disabled={!noteValid}
              onClick={handleConfirm}
              data-testid="postif-approval-confirm"
            >
              {mode === "decline" ? "Decline this hire" : "Send back for changes"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PifApprovalDecisionPanel;
